/**
 * Auszeichnung im Chat — fett, kursiv, durchgestrichen, Kapitälchen.
 *
 * **Zeichen im Text und kein Markup.** Was gespeichert wird, ist genau das,
 * was jemand getippt hat: `Das war *großartig*`. Der Grund ist nicht
 * Bequemlichkeit, sondern dass derselbe Text noch an zwei Stellen auftaucht,
 * an denen es keine Formatierung gibt — in der Vorschau der Chatliste und auf
 * dem Sperrbildschirm. Stünde dort `<b>großartig</b>`, läse man das.
 *
 * Beide baut der Server, und dort steht `Auszeichnung::ohneZeichen()`; sie
 * nimmt die Sternchen weg und lässt das Wort stehen. Die Kopie hier wurde
 * nirgends gerufen und ist seit 1.101.0 weg.
 *
 * **Kein HTML, an keiner Stelle.** Der Text kommt von Menschen und geht an
 * Menschen; er wird in Stücke zerlegt und als Text gesetzt, nie über
 * `dangerouslySetInnerHTML`. Ein Chat, in dem jemand Markup unterbringen
 * kann, ist keiner.
 */

/**
 * Die vier Zeichen und was sie bedeuten.
 *
 * **Ohne Namen** (1.69.0): die standen hier als `name: 'Fett'` und waren damit
 * Oberflächentext in `lib/` -- gegen die Regel, und vom Textwächter nicht
 * gesehen, weil eine kurze Zeichenkette ohne Leerzeichen seit 1.64.0 mit
 * Ansage durchgeht. Sie stehen jetzt in `texte/de.ts` unter
 * `chat.auszeichnung`, samt der Beschriftung für den Knopf.
 */
export const AUSZEICHNUNGEN = [
  { zeichen: '*', art: 'fett' as const },
  { zeichen: '_', art: 'kursiv' as const },
  { zeichen: '~', art: 'durch' as const },
  { zeichen: '^', art: 'kapitael' as const },
]

export type Art = 'text' | 'fett' | 'kursiv' | 'durch' | 'kapitael'

export interface Stueck {
  art: Art
  text: string
}

/**
 * Die Zeichen, die eine Auszeichnung öffnen und schließen.
 *
 * Als Zeichenklasse für den regulären Ausdruck — `^` muss dabei escaped
 * werden, sonst verneint es die Klasse.
 */
const ZEICHEN: Record<string, Art> = {
  '*': 'fett',
  _: 'kursiv',
  '~': 'durch',
  '^': 'kapitael',
}

/**
 * Ein Text wird zu einer Folge von Stücken.
 *
 * **Nicht verschachtelt, und das ist Absicht.** `*fett und _kursiv_*` ergibt
 * fett mit sichtbaren Unterstrichen. Verschachtelung brauchte einen richtigen
 * Zerteiler; in einer Nachricht an Familie ist sie das nicht wert, und die
 * halbe Umsetzung wäre schlimmer als keine.
 *
 * **Ein Zeichen ohne Partner bleibt stehen.** Wer „3 * 4" schreibt, meint
 * mal — und bekommt „3 * 4". Deshalb greift die Auszeichnung nur, wenn ein
 * zweites Zeichen derselben Sorte folgt und dazwischen etwas steht, das kein
 * Zeilenumbruch ist.
 */
export function zerlege(text: string): Stueck[] {
  const stuecke: Stueck[] = []
  let rest = text
  // Kein `g`-Flag mit `exec` in einer Schleife: der Zustand des Ausdrucks
  // wäre dann geteilt, und das ist eine bekannte Falle.
  const muster = /([*_~^])([^\n*_~^]+)\1/

  for (;;) {
    const treffer = muster.exec(rest)
    if (treffer === null || treffer.index === undefined) break

    if (treffer.index > 0) {
      stuecke.push({ art: 'text', text: rest.slice(0, treffer.index) })
    }
    stuecke.push({ art: ZEICHEN[treffer[1]!] ?? 'text', text: treffer[2]! })
    rest = rest.slice(treffer.index + treffer[0].length)
  }

  if (rest !== '') stuecke.push({ art: 'text', text: rest })
  return stuecke
}

/**
 * **Hier stand `packe()`, und es wird nicht mehr gebraucht.**
 *
 * Sie setzte die Zeichen um eine Auswahl im `<textarea>` herum — `*Wort*`.
 * Seit die Eingabezeile ein `contenteditable` ist und die Auszeichnung
 * sofort zeigt, was sie meint, legt `reichtext.ts` sie als Element an und
 * macht daraus beim Senden wieder Zeichen. Eine zweite Kopie derselben
 * Regel stehen zu lassen, „falls doch mal", ist genau der Weg, auf dem zwei
 * Kopien auseinanderlaufen.
 */
